import { Company, DataInstance, Messages, TopicBuilder } from "@busy-human/amt-library";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { useAuth } from "./useAuth";
import emitter, { events } from "@/events";
import { useUsers } from "./useUsers";
import { arrayRemove, Timestamp } from "firebase/firestore";
import { isEqual } from "lodash";
import { storage } from "@/util/firebase";
import { ref as storageRef, uploadBytes, getBlob, getDownloadURL } from 'firebase/storage';
import { NotificationHandled, useMessaging } from "@/util/messaging";
import { useRouter } from "vue-router";

export const useMessages = defineStore('messages', () => {
	const auth = useAuth();
	const messaging = useMessaging();
	// const messages = ref(new Map<string, DataInstance<Messages.Message>>());
	const loaded = ref(false);
	const unsub = ref<(() => void) | null>(null)

	const waitingPromises = ref<(() => void)[]>([])

	watch(() => auth.companyId, async (companyId, prevCompanyId) => {
		if (prevCompanyId && companyId !== prevCompanyId) {
			await messaging.removeTopicHandler(TopicBuilder.CommsMessage(prevCompanyId));
		}

		if (companyId && companyId !== prevCompanyId) {
			await messaging.addTopicHandler<{ triggeredBy: string, unitId: string }>(TopicBuilder.CommsMessage(companyId), {
				onReceived: (data) => {
					if (data.triggeredBy === auth.currentUID) return NotificationHandled.Ignored;
				},
				onClicked: (data) => {
					emitter.emit("PUSHROUTE", `/messaging?unitID=${data.unitId}`)
				}
			});
		}
	}, { immediate: true });

	function getCollection() {
		if (!auth.companyId) throw new Error("No companyId!");
		return Company.Companies.subCollectionEntity(auth.companyId, 'messages')
	}

	function waitForReady() {
		if (!loaded.value) return new Promise<void>(res => waitingPromises.value.push(res))
	}

	async function markAllMessagesRead() {
		if (!auth.companyId) return;
		if (!auth.currentUID) return;
		const col = Company.Companies.subCollectionEntity(auth.companyId, 'messages');

		const msgs = await col.subset((_, query) => query
			.where('usersUnread', 'array-contains', auth.currentUID)
			.toQuery()
		).fetch();

		const tmp = arrayRemove(auth.currentUID) as any;

		const res = await Promise.allSettled(msgs.map(m =>
			m.update({
				usersUnread: tmp
			})
		));

		for (const r of res) {
			if (r.status === 'rejected') console.error(r.reason);
		}
	}

	async function sendMessage(message: string, to: string, attachments?: string[], incidentId?: string | null, attachmentNote?: string | null) {
		const collection = getCollection();
		const userStore = useUsers();
		await userStore.waitForReady();
		const current = userStore.currentUser;
		if (!current) throw new Error("You are not a user");
		const messageData: Messages.Message = {
			...collection.defaults(),
			unitId: to,
			message,
			sender: current?.name.fullName,
			senderId: current.$id,
			commId: current.$id,
			from: "company",
			timestamp: Timestamp.now(),
			usersUnread: [...userStore.users.keys()].filter(k => k !== auth.currentUID),
		}
		if (incidentId) messageData.incidentId = incidentId;
		if (attachmentNote) messageData.attachmentNote = attachmentNote;
		if (attachments) messageData.attachments = attachments;
		await collection.add(messageData);
	}

	const str = storage(`gs://${CONFIG.firebase.bucket.attachments}`)

	async function uploadAttachment(file: File) {
		const fileId = crypto.randomUUID();
		const ext = file.name.split(".").pop();
		const companyId = auth.companyId;
		if (!companyId) throw new Error("Not in a company");

		const fileRef = `${companyId}/${fileId}.${ext}`;
		await uploadBytes(storageRef(str, fileRef), file);

		return fileRef;
	}

	const attachmentUrls = ref(new Map<string, string>());
	async function getAttachmentUrl(attRef: string) {
		if (!attRef) return "";
		const existing = attachmentUrls.value.get(attRef);
		if (existing) return existing;
		const newUrl = await getDownloadURL(storageRef(str, attRef));
		// const blob = await getBlob(storageRef(str, attRef));
		// const newUrl = URL.createObjectURL(blob);
		attachmentUrls.value.set(attRef, newUrl);
		return newUrl;
	}

	return { loaded, waitForReady, sendMessage, uploadAttachment, getAttachmentUrl, markAllMessagesRead }
}) 